(() => {
  const chooserSelector = '[data-blaze-offer-chooser]';
  const legacyOfferType = 'buy1_get1';
  const tieredOfferType = 'buy2_get3';

  const getConfig = () => ({
    enabled: document.body?.dataset.blazePairedOfferEnabled === 'true',
    tieredEnabled: document.body?.dataset.blazeTieredOfferEnabled === 'true',
    productId: document.body?.dataset.blazePairedOfferProductId,
    copy: {
      selectorTitle: document.body?.dataset.blazeOfferSelectorTitle || 'Choose your offer',
      offerOneTitle: document.body?.dataset.blazeOfferOneTitle || 'Buy 1 Get 1 Free',
      offerTwoTitle: document.body?.dataset.blazeOfferTwoTitle || 'Buy 2 Get 3 Free',
      selectOffer: document.body?.dataset.blazeOfferSelect || 'Select {{ offer }}',
      selected: document.body?.dataset.blazeOfferSelected || 'Selected offer',
      quantityTitle: document.body?.dataset.blazePairedOfferTitle || 'Choose the number of paid bottles',
      decrease: document.body?.dataset.blazePairedOfferDecrease || 'Decrease the number of paid bottles',
      increase: document.body?.dataset.blazePairedOfferIncrease || 'Increase the number of paid bottles',
      quantityInput: document.body?.dataset.blazePairedOfferQuantityInput || 'Number of paid bottles',
      summary:
        document.body?.dataset.blazePairedOfferSummary ||
        "You'll receive {{ total }} bottles: {{ paid }} paid + {{ free }} free",
      switchError:
        document.body?.dataset.blazeOfferSwitchError ||
        "We couldn't prepare the selected offer in your cart. Please try again.",
    },
  });

  const escapeHtml = (value) =>
    String(value)
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#039;');

  const replaceTokens = (template, values) =>
    Object.entries(values).reduce(
      (result, [key, value]) => result.replaceAll(`{{ ${key} }}`, String(value)),
      template || ''
    );

  const getOffers = (config = getConfig()) => {
    const offers = [
      {
        type: legacyOfferType,
        title: config.copy.offerOneTitle,
        paidStep: 1,
        freePerStep: 1,
        initialPaid: 1,
      },
    ];

    if (config.tieredEnabled) {
      offers.push({
        type: tieredOfferType,
        title: config.copy.offerTwoTitle,
        paidStep: 2,
        freePerStep: 3,
        initialPaid: 2,
      });
    }

    return offers;
  };

  const getOffer = (type) => getOffers().find((offer) => offer.type === type) || getOffers()[0];

  const normalisePaidQuantity = (value, offer) => {
    const parsed = Number.parseInt(value, 10);
    if (!Number.isFinite(parsed)) return offer.initialPaid;
    return Math.max(offer.initialPaid, Math.ceil(parsed / offer.paidStep) * offer.paidStep);
  };

  const calculateOffer = (offer, paidQuantity) => {
    const paid = normalisePaidQuantity(paidQuantity, offer);
    const bundleCount = paid / offer.paidStep;
    const free = bundleCount * offer.freePerStep;
    return { paid, free, total: paid + free };
  };

  const updateCard = (card, paidQuantity) => {
    const offer = getOffer(card.dataset.blazeOfferType);
    const quantities = calculateOffer(offer, paidQuantity);
    const input = card.querySelector('[data-blaze-offer-input]');
    const summary = card.querySelector('[data-blaze-offer-summary]');
    const minus = card.querySelector('[data-blaze-offer-minus]');

    card.dataset.blazePaidQuantity = quantities.paid.toString();
    if (input instanceof HTMLInputElement) input.value = quantities.paid.toString();
    if (summary) {
      summary.textContent = replaceTokens(getConfig().copy.summary, quantities);
    }
    if (minus instanceof HTMLButtonElement) minus.disabled = quantities.paid <= offer.initialPaid;

    return quantities;
  };

  const selectOffer = (chooser, offerType, paidQuantity) => {
    const selectedCard = chooser.querySelector(`[data-blaze-offer-card][data-blaze-offer-type="${offerType}"]`);
    if (!(selectedCard instanceof HTMLElement)) return;

    const quantities = updateCard(selectedCard, paidQuantity ?? selectedCard.dataset.blazePaidQuantity);
    chooser.dataset.blazeSelectedOffer = offerType;

    chooser.querySelectorAll('[data-blaze-offer-card]').forEach((card) => {
      const isSelected = card === selectedCard;
      card.classList.toggle('is-selected', isSelected);
      const selectButton = card.querySelector('[data-blaze-offer-select]');
      if (selectButton instanceof HTMLButtonElement) selectButton.setAttribute('aria-pressed', isSelected.toString());
      card.querySelector('[data-blaze-offer-selected-label]')?.toggleAttribute('hidden', !isSelected);
    });

    const form = chooser.closest('form');
    const quantityInput = form?.querySelector('input[name="quantity"]');
    const offerTypeInput = form?.querySelector('input[name="properties[_blaze_offer_type]"]');
    if (quantityInput instanceof HTMLInputElement) quantityInput.value = quantities.total.toString();
    if (offerTypeInput instanceof HTMLInputElement) offerTypeInput.value = offerType;
  };

  const syncProductControls = (productId, offerType, paidQuantity) => {
    document.querySelectorAll(chooserSelector).forEach((chooser) => {
      if (chooser instanceof HTMLElement && chooser.dataset.blazeOfferProductId === productId) {
        selectOffer(chooser, offerType, paidQuantity);
      }
    });
  };

  const getCartUrl = (suffix) => `${window.Theme?.routes?.cart_url || '/cart'}${suffix}`;
  const getCartUpdateUrl = () => window.Theme?.routes?.cart_update_url || '/cart/update.js';

  const removeConflictingOffers = async (productId, selectedOfferType) => {
    const cartResponse = await fetch(getCartUrl('.js'), { credentials: 'same-origin' });
    if (!cartResponse.ok) throw new Error('Unable to load cart before switching offers');

    const cart = await cartResponse.json();
    const updates = Object.fromEntries(
      cart.items
        .filter((item) => {
          if (item.properties?._blaze_paired_offer !== 'true') return false;
          if (String(item.product_id) !== String(productId)) return false;
          const existingOfferType = item.properties?._blaze_offer_type || legacyOfferType;
          return existingOfferType !== selectedOfferType;
        })
        .map((item) => [item.key, 0])
    );

    if (Object.keys(updates).length === 0) return;

    const updateResponse = await fetch(getCartUpdateUrl(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ updates }),
    });
    if (!updateResponse.ok) throw new Error('Unable to remove the previous offer');
  };

  const addOfferSwitchGuard = (form, productId) => {
    if (!getConfig().tieredEnabled) return;
    form.dataset.blazeOfferSwitchGuard = 'true';
    form.dataset.blazeOfferProductId = productId;
  };

  const handleOfferSubmit = async (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || form.dataset.blazeOfferSwitchGuard !== 'true') return;

    if (form.dataset.blazeOfferSubmitReady === 'true') {
      delete form.dataset.blazeOfferSubmitReady;
      return;
    }

    // Horizon handles declarative form submissions on document capture. Intercepting at window
    // prevents that first request before the cart is prepared, then one guarded submit is allowed.
    event.preventDefault();
    event.stopImmediatePropagation();
    if (form.dataset.blazeOfferSwitching === 'true') return;

    form.dataset.blazeOfferSwitching = 'true';
    const submitter = event.submitter instanceof HTMLButtonElement ? event.submitter : undefined;
    const offerTypeInput = form.querySelector('input[name="properties[_blaze_offer_type]"]');
    const selectedOfferType = offerTypeInput instanceof HTMLInputElement ? offerTypeInput.value : legacyOfferType;
    const productId = form.dataset.blazeOfferProductId;

    try {
      await removeConflictingOffers(productId, selectedOfferType);
      form.dataset.blazeOfferSubmitReady = 'true';
      form.requestSubmit(submitter);
    } catch (error) {
      console.error('[blaze-offer-switch]', error);
      const component = form.closest('product-form-component');
      const liveRegion = component?.querySelector('[ref="liveRegion"]');
      if (liveRegion) liveRegion.textContent = getConfig().copy.switchError;
      window.alert(getConfig().copy.switchError);
    } finally {
      delete form.dataset.blazeOfferSwitching;
    }
  };

  const createOfferCard = (offer, copy, isSelected) => {
    const selectLabel = replaceTokens(copy.selectOffer, { offer: offer.title });
    const inputLabel = `${copy.quantityInput}: ${offer.title}`;
    const decreaseLabel = `${copy.decrease}: ${offer.title}`;
    const increaseLabel = `${copy.increase}: ${offer.title}`;

    return `
      <div class="blaze-offer-card${isSelected ? ' is-selected' : ''}" data-blaze-offer-card data-blaze-offer-type="${escapeHtml(offer.type)}" data-blaze-paid-quantity="${offer.initialPaid}">
        <button class="blaze-offer-card__select" type="button" data-blaze-offer-select aria-pressed="${isSelected}" aria-label="${escapeHtml(selectLabel)}">
          <span class="blaze-offer-card__title">${escapeHtml(offer.title)}</span>
          <span class="blaze-offer-card__selected" data-blaze-offer-selected-label${isSelected ? '' : ' hidden'}>${escapeHtml(copy.selected)}</span>
        </button>
        <p class="blaze-offer-card__quantity-label">${escapeHtml(copy.quantityTitle)}</p>
        <div class="blaze-paired-quantity__controls">
          <button type="button" class="blaze-paired-quantity__button" data-blaze-offer-minus aria-label="${escapeHtml(decreaseLabel)}">−</button>
          <input class="blaze-paired-quantity__input" data-blaze-offer-input type="number" min="${offer.initialPaid}" step="${offer.paidStep}" inputmode="numeric" aria-label="${escapeHtml(inputLabel)}" value="${offer.initialPaid}">
          <button type="button" class="blaze-paired-quantity__button" data-blaze-offer-plus aria-label="${escapeHtml(increaseLabel)}">+</button>
        </div>
        <p class="blaze-paired-quantity__summary" data-blaze-offer-summary aria-live="polite"></p>
      </div>
    `;
  };

  const enhanceForm = (component) => {
    const config = getConfig();
    const { enabled, productId, copy } = config;
    if (!enabled || !productId || component.hasAttribute('data-blaze-sticky-form') || component.dataset.productId !== productId || component.dataset.blazePairedOfferReady === 'true') {
      return;
    }

    const form = component.querySelector('form[data-type="add-to-cart-form"]');
    const addToCart = form?.querySelector('add-to-cart-component');
    if (!(form instanceof HTMLFormElement) || !addToCart) return;

    component.dataset.blazePairedOfferReady = 'true';
    component.dataset.blazePairedOfferForm = 'true';
    form.classList.add('blaze-paired-offer-form');

    const pairedMarker = document.createElement('input');
    pairedMarker.type = 'hidden';
    pairedMarker.name = 'properties[_blaze_paired_offer]';
    pairedMarker.value = 'true';
    form.append(pairedMarker);

    const offerTypeMarker = document.createElement('input');
    offerTypeMarker.type = 'hidden';
    offerTypeMarker.name = 'properties[_blaze_offer_type]';
    offerTypeMarker.value = legacyOfferType;
    form.append(offerTypeMarker);

    const chooser = document.createElement('fieldset');
    chooser.className = 'blaze-offer-selector';
    chooser.dataset.blazeOfferChooser = '';
    chooser.dataset.blazeOfferProductId = productId;
    chooser.innerHTML = `
      <legend class="blaze-offer-selector__legend">${escapeHtml(copy.selectorTitle)}</legend>
      <div class="blaze-offer-selector__cards">
        ${getOffers(config)
          .map((offer, index) => createOfferCard(offer, copy, index === 0))
          .join('')}
      </div>
    `;

    addToCart.before(chooser);
    chooser.querySelectorAll('[data-blaze-offer-card]').forEach((card) => {
      const offerType = card.dataset.blazeOfferType;
      const offer = getOffer(offerType);
      const input = card.querySelector('[data-blaze-offer-input]');
      const selectCard = () => {
        const paid = input instanceof HTMLInputElement ? input.value : card.dataset.blazePaidQuantity;
        syncProductControls(productId, offerType, paid);
      };

      card.querySelector('[data-blaze-offer-select]')?.addEventListener('click', selectCard);
      card.querySelector('[data-blaze-offer-minus]')?.addEventListener('click', () => {
        const current = input instanceof HTMLInputElement ? normalisePaidQuantity(input.value, offer) : offer.initialPaid;
        syncProductControls(productId, offerType, current - offer.paidStep);
      });
      card.querySelector('[data-blaze-offer-plus]')?.addEventListener('click', () => {
        const current = input instanceof HTMLInputElement ? normalisePaidQuantity(input.value, offer) : offer.initialPaid;
        syncProductControls(productId, offerType, current + offer.paidStep);
      });
      input?.addEventListener('change', selectCard);
      input?.addEventListener('blur', selectCard);
      updateCard(card, offer.initialPaid);
    });

    selectOffer(chooser, legacyOfferType, 1);
    addOfferSwitchGuard(form, productId);
  };

  const enhanceAll = (scope = document) => {
    scope.querySelectorAll('product-form-component[data-product-id]').forEach(enhanceForm);
  };

  const enhanceStickyAddToCart = (sticky) => {
    const { enabled, productId } = getConfig();
    if (!enabled || sticky.dataset.productId !== productId || sticky.dataset.blazePairedOfferReady === 'true') return;

    const stickyButton = sticky.querySelector('[ref="addToCartButton"]');
    if (!(stickyButton instanceof HTMLButtonElement)) return;

    sticky.dataset.blazePairedOfferReady = 'true';
    sticky.addEventListener(
      'click',
      (event) => {
        if (!(event.target instanceof Element) || !event.target.closest('[ref="addToCartButton"]')) return;
        event.preventDefault();
        event.stopPropagation();
        enhanceAll();
        const target = document.querySelector(
          `product-form-component[data-product-id="${CSS.escape(productId)}"][data-blaze-paired-offer-form="true"] button[type="submit"]`
        );
        if (target instanceof HTMLButtonElement) target.click();
      },
      true
    );
  };

  const init = () => {
    window.addEventListener('submit', handleOfferSubmit, true);
    enhanceAll();
    document.querySelectorAll('sticky-add-to-cart[data-product-id]').forEach(enhanceStickyAddToCart);
    new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (!(node instanceof Element)) return;
          if (node.matches('product-form-component[data-product-id]')) enhanceForm(node);
          if (node.matches('sticky-add-to-cart[data-product-id]')) enhanceStickyAddToCart(node);
          enhanceAll(node);
        });
      });
    }).observe(document.body, { childList: true, subtree: true });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
